<template>
  <div class="login-container">

    <div>
       <vue-particles
      color="#eeeeee"
      :particle-opacity="0.5"
      :particles-number="100"
      shape-type="star"
      :particle-size="2"
      lines-color="#3D474C"
      :lines-width="1"
      :line-linked="true"
      :line-opacity="0.4"
      :lines-distance="80"
      :move-speed="1"
      :hover-effect="true"
      hover-mode="repulse"
      :click-effect="true"
      click-mode="push"
    >

    </vue-particles>

     <b-overlay
      :show="loading"
          :opacity="0.2"
          rounded="sm"
      spinner-variant="primary"
      class="d-inline-block"
    >

    <div
    class="login-form"
    >
      <div class="title-container">
        <h3 class="title ">Welcome to Gively</h3>
        <p v-if="otpStep" class="text-white-50 small">Enter the verification code sent to your email.</p>
        <p v-if="totpStep" class="text-white-50 small">Enter the code from your authenticator app, or a recovery code.</p>
        <p v-if="enrollStep" class="text-white-50 small">Privileged accounts need an authenticator before the back office opens.</p>
      </div>

            <div v-if="!otpStep && !totpStep && !enrollStep">
              <b-form-input v-model="loginForm.username" placeholder="email"></b-form-input>
            </div>

            <div class="mt-3" v-if="!otpStep && !totpStep && !enrollStep">
                <b-form-input type="password" v-model="loginForm.password" placeholder="password"></b-form-input>
            </div>

            <div class="mt-3" v-if="otpStep">
                <b-form-input v-model="loginForm.otp" placeholder="6-digit verification code" maxlength="6"></b-form-input>
            </div>

            <div class="mt-3" v-if="totpStep">
                <b-form-input v-model="loginForm.totp" placeholder="Authenticator or recovery code"></b-form-input>
            </div>

            <div class="mt-3 text-white" v-if="enrollStep">
                <div v-if="enrollment.secret && !recoveryCodes.length">
                  <p class="small text-white-50 mb-2">Scan the QR code, or copy the setup key into your authenticator app.</p>
                  <div class="auth-qr" v-if="qrDataUrl">
                    <img :src="qrDataUrl" width="180" height="180" alt="Authenticator QR code" />
                  </div>
                  <label class="small d-block text-white-50 mt-2 mb-1" for="auth-setup-key">Setup key</label>
                  <b-input-group class="auth-key">
                    <b-form-input
                      id="auth-setup-key"
                      ref="setupKey"
                      :value="enrollment.secret"
                      readonly
                    />
                    <b-input-group-append>
                      <b-button variant="outline-primary" @click="copyKey">Copy</b-button>
                    </b-input-group-append>
                  </b-input-group>
                  <b-form-input class="mt-2" v-model="loginForm.totp" placeholder="6-digit authenticator code" maxlength="6"></b-form-input>
                </div>
                <div v-if="recoveryCodes.length" class="mt-2 small">
                  <div>Save these recovery codes. They are shown once.</div>
                  <div v-for="code in recoveryCodes" :key="code">{{ code }}</div>
                </div>
            </div>

            <b-button  v-ripple.400="'rgba(186, 191, 199, 0.15)'" variant="outline-primary text-white" class="mt-3" @click="submitStep()">
              {{ enrollStep ? (recoveryCodes.length ? 'Continue' : 'Confirm authenticator') : (totpStep || otpStep ? 'Verify' : 'Login') }}
            </b-button>
            <b-button v-if="otpStep" variant="link" class="mt-2 text-white-50" @click="resendOtp()">Resend code</b-button>
      </div>
          </b-overlay>
    </div>
    </div>
</template>

<script>
import QRCode from 'qrcode'
import Ripple from 'vue-ripple-directive'
import { BOverlay, BButton } from 'bootstrap-vue'
import commonApi from "@/services/api";
import { getAbilitiesForRole, normalizeStaffRole } from '@/libs/acl/staffAbilities';

export default {
  name: "Login",
  components:{BOverlay, BButton},
  directives: {
    Ripple,
  },
  data() {

    return {
      loginForm: {
        username: "",
        password: "",
        otp: "",
        totp: "",
      },
      loading: false,
      otpStep: false,
      totpStep: false,
      enrollStep: false,
      enrollment: {},
      qrDataUrl: '',
      recoveryCodes: [],
      enrolledUser: null,
      passwordType: "password",
      redirect: undefined
    };
  },
  watch: {
    $route: {
      handler: function(route) {
        this.redirect = route.query && route.query.redirect;
      },
      immediate: true
    }
  },
  methods: {
    rememberCsrf(resp) {
      if (resp && resp.data && resp.data.csrfToken) sessionStorage.setItem('gively-csrf', resp.data.csrfToken)
    },
    completeLogin(resp) {
      localStorage.removeItem('accessToken')
      this.rememberCsrf(resp)
      commonApi.setDynamicToken();

      const user = resp.data.user || {};
      const ability = getAbilitiesForRole(user.role);
      const normalizedRole = normalizeStaffRole(user.role);

      user.fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      user.username = user.email || user.firstName;
      user.role = normalizedRole;
      user.ability = ability;

      this.$ability.update(ability)
      localStorage.setItem('userData', JSON.stringify(user))
      this.$router.push('/').catch();
    },

    async handleLogin()  {
        if(!this.loginForm.username || !this.loginForm.password){
          this.$bvToast.toast("Field Cannot be Empty", {
            title: `Error`,
            solid: false,
            toaster: 'b-toaster-top-center',
            variant: 'primary',
          });
          return false;
        }

        this.loading = true;
        try {
          const resp = await commonApi.login({...this.loginForm, email: this.loginForm.username});
          if(resp && resp.success) {
            if (resp.data.requiresOtp) {
              this.otpStep = true;
              return;
            }
            this.completeLogin(resp);
          }
        } finally {
          this.loading = false;
        }
    },

    async verifyOtp() {
      if (!this.loginForm.otp) {
        this.$bvToast.toast('Enter the verification code', { title: 'Error', variant: 'danger' });
        return;
      }
      this.loading = true;
      try {
        const resp = await commonApi.verifyLoginOtp({
          email: this.loginForm.username,
          password: this.loginForm.password,
          otp: this.loginForm.otp,
        });
        if (resp && resp.success) {
          this.rememberCsrf(resp);
          if (resp.data.requiresTotpEnrollment) {
            this.otpStep = false;
            this.enrollStep = true;
            const setup = await commonApi.setupStaffMfa();
            if (setup && setup.success) {
              this.enrollment = setup.data || {};
              await this.drawQr();
            }
            return;
          }
          if (resp.data.requiresTotp) {
            this.otpStep = false;
            this.totpStep = true;
            return;
          }
          this.completeLogin(resp);
        }
      } finally {
        this.loading = false;
      }
    },

    submitStep() {
      if (this.enrollStep) return this.confirmEnrollment();
      if (this.totpStep) return this.verifyTotp();
      if (this.otpStep) return this.verifyOtp();
      return this.handleLogin();
    },

    async verifyTotp() {
      this.loading = true;
      try {
        const resp = await commonApi.verifyLoginTotp({
          code: this.loginForm.totp,
          recoveryCode: this.loginForm.totp,
        });
        if (resp && resp.success) this.completeLogin(resp);
      } finally {
        this.loading = false;
      }
    },

    async confirmEnrollment() {
      if (this.recoveryCodes.length) {
        this.completeLogin({ data: { user: this.enrolledUser, csrfToken: sessionStorage.getItem('gively-csrf') } });
        return;
      }
      this.loading = true;
      try {
        const resp = await commonApi.confirmStaffMfa({ code: this.loginForm.totp });
        if (resp && resp.success) {
          this.recoveryCodes = (resp.data && resp.data.recoveryCodes) || [];
          this.enrolledUser = resp.data.user;
          this.rememberCsrf(resp);
        }
      } finally {
        this.loading = false;
      }
    },

    async drawQr() {
      const url = this.enrollment && this.enrollment.otpauthUrl;
      if (!url) {
        this.qrDataUrl = '';
        return;
      }
      try {
        this.qrDataUrl = await QRCode.toDataURL(url, {
          width: 180,
          margin: 1,
          errorCorrectionLevel: 'M',
        });
      } catch (error) {
        this.qrDataUrl = '';
      }
    },

    async copyKey() {
      const key = (this.enrollment && this.enrollment.secret) || '';
      if (!key) return;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(key);
        } else {
          const field = this.$refs.setupKey && (this.$refs.setupKey.$el || this.$refs.setupKey);
          const input = field && (field.tagName === 'INPUT' ? field : field.querySelector('input'));
          if (!input) throw new Error('copy unavailable');
          input.focus();
          input.select();
          document.execCommand('copy');
        }
        this.$bvToast.toast('Setup key copied', { title: 'Copied', variant: 'success' });
      } catch (error) {
        this.$bvToast.toast('Select the setup key and copy it', { title: 'Copy the key', variant: 'warning' });
      }
    },

    async resendOtp() {
      await commonApi.resendLoginOtp({
        email: this.loginForm.username,
        password: this.loginForm.password,
      });
      this.$bvToast.toast('Verification code resent', { title: 'Success', variant: 'success' });
    },
  }
};
</script>

<style lang="scss">

$bg: #283443;
$light_gray: #fff;
$cursor: #fff;

@supports (-webkit-mask: none) and (not (cater-color: $cursor)) {
  .login-container .el-input input {
    color: $cursor;
  }
}

/* reset element-ui css */
.login-container {
  text-align: center;
  height: 100vh;

  .el-input {
    display: inline-block;
    height: 47px;
    width: 85%;

    input {
      background: transparent;
      border: 0px;
      -webkit-appearance: none;
      border-radius: 0px;
      padding: 12px 5px 12px 15px;
      color: $light_gray;
      height: 47px;
      caret-color: $cursor;

      &:-webkit-autofill {
        box-shadow: 0 0 0px 1000px $bg inset !important;
        -webkit-text-fill-color: $cursor !important;
      }
    }
  }

  .el-form-item {
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.1);
    border-radius: 5px;
    color: #454545;
  }
}
</style>

<style lang="scss" scoped>
$bg: rgba(10, 19, 31, 1);
$dark_gray: #889aa4;
$light_gray: #eee;

.particles-js {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9;
  width: 100vw;
  height: 100vh;
}

.login-container {
  min-height: 100%;
  width: 100%;
  background-color: $bg;
  overflow: hidden;

  .login-form {
    position: fixed;
    top: 50%;
    left: 50%;
    z-index: 999;
    width: 520px;
    max-width: 100%;
    padding: 45px;
    border-radius: 20px;
    transform: translate(-50%,-51%);
    overflow: auto;
    max-height: 100vh;
    background: rgba(18, 28, 40, 0.5);
  }

  .auth-qr {
    display: inline-block;
    background: #fff;
    padding: 8px;
    border-radius: 8px;
    line-height: 0;
  }

  .auth-key {
    text-align: left;
  }

  .tips {
    font-size: 14px;
    color: #fff;
    margin-bottom: 10px;

    span {
      &:first-of-type {
        margin-right: 16px;
      }
    }
  }

  .svg-container {
    padding: 6px 5px 6px 15px;
    color: $dark_gray;
    vertical-align: middle;
    width: 30px;
    display: inline-block;
  }

  .title-container {
    position: relative;

    .title {
      font-size: 26px;
      color: $light_gray;
      margin: 0px auto 40px auto;
      text-align: center;
      font-weight: bold;
    }
  }

  .show-pwd {
    position: absolute;
    right: 10px;
    top: 7px;
    font-size: 16px;
    color: $dark_gray;
    cursor: pointer;
    user-select: none;
  }
}
</style>
